/**
 * Session-level batch save for pilot/instructor assessments.
 */

import {
  createVersion,
  type AssessmentVersion,
  type BatchAssessmentUpdate,
} from './assessment-versions';

export interface SaveSessionBatchParams {
  sessionId: string;
  assessmentType: 'pilot' | 'instructor';
  batchUpdates: BatchAssessmentUpdate[];
  userId: string | null;
  changeSummary?: string;
}

/**
 * Persists all dirty assessment rows and inserts one session-level version snapshot.
 * Requires migration 211+ (p_batch_updates + p_session_id).
 */
export async function saveSessionBatch(
  params: SaveSessionBatchParams,
): Promise<AssessmentVersion> {
  const { sessionId, assessmentType, batchUpdates, userId, changeSummary } = params;

  if (!sessionId) {
    throw new Error('sessionId is required for batch save');
  }
  if (batchUpdates.length === 0) {
    throw new Error('No changes to save');
  }

  const versionRecord = await createVersion(
    batchUpdates[0]!.assessmentId,
    assessmentType,
    null,
    userId,
    {
      isAiBaseline: false,
      sessionId,
      batchUpdates,
      changeSummary: changeSummary ?? 'Manual edit from web',
    },
  );

  if (!versionRecord) {
    throw new Error('Failed to save assessment version');
  }

  return versionRecord;
}
